import { getToken } from '@/utils/session';

/**
 * Options to control PDF export. Keep this minimal on purpose.
 * - padding: inner whitespace for the exported content (used as page margins in A4 mode).
 */
export interface ExportOptions {
    padding?: string;
    contentWidthPx?: number;
}

const { host } = window.location;

// Detect if we likely need Docker Desktop host mapping (Mac/Windows + served from localhost)
const needsDockerDesktopHostMap = (): boolean => {
    const ua = navigator.userAgent || '';
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    const isMacOrWin = /Macintosh|Mac OS X|Windows|Win64/i.test(ua);
    return isLocal && isMacOrWin;
};

// Resolve host for assets referenced in CSS url(...)
const resolveAssetHostname = (hostname: string): string => {
    if (needsDockerDesktopHostMap() && (hostname === 'localhost' || hostname === '127.0.0.1')) {
        // For Docker Desktop (Mac/Windows), containers reach the host via this DNS name
        return 'host.docker.internal';
    }
    return hostname;
};

/**
 * Rewrites relative asset URLs found in CSS to absolute URLs based on baseHref.
 * Also maps localhost/127.0.0.1 to host.docker.internal when running Docker on macOS/Windows (no-op in prod).
 */
const absolutizeCssUrls = (css: string, baseHref: string): string => {
    const urlRegex = /url\(([^)]+)\)/g;
    const importRegex = /@import\s+(["'])([^"']+)\1/g;

    const toAbsolute = (raw: string) => {
        const cleaned = raw.trim().replace(/^['"]|['"]$/g, '');
        if (/^data:|^http(s?):|^blob:|^about:/.test(cleaned)) {
            return cleaned;
        }
        const abs = new URL(cleaned, baseHref);
        abs.hostname = resolveAssetHostname(abs.hostname); // Docker Desktop dev-only rewrite
        return abs.toString();
    };

    let out = css.replace(urlRegex, (_, p1) => `url(${toAbsolute(p1)})`);
    out = out.replace(importRegex, (_, q, p2) => `@import "${toAbsolute(p2)}"`);
    return out;
};

/**
 * Gathers inline <style> blocks and fetches <link rel="stylesheet"> content,
 * returning one concatenated CSS string suitable for inlining into the HTML sent to the service.
 */
const buildInlineCss = async (): Promise<string> => {
    const inlineHeadCss = Array.from(document.querySelectorAll('style'))
        .map(style => style.textContent || '')
        .filter(Boolean)
        .join('\n');

    const hrefs = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map(link => (link as HTMLLinkElement).href)
        .filter(Boolean);

    const linkedCss = await Promise.all(
        hrefs.map(async href => {
            const res = await fetch(href);
            if (!res.ok) {
                return '';
            }
            const css = await res.text();
            return absolutizeCssUrls(css, href);
        })
    ).then(parts => parts.filter(Boolean).join('\n'));

    return [inlineHeadCss, linkedCss].filter(Boolean).join('\n');
};

/**
 * Wraps provided markup into a minimal HTML document. Applies a fixed desktop width
 * to preserve screen layout; scaling to A4 happens server-side via Puppeteer options.
 * Also embeds original stylesheet <link> tags to avoid losing styles when CORS prevents
 * fetching CSS text in the browser.
 */
const buildHtmlDocument = (
    markup: string,
    title = 'Export',
    inlineCss = '',
    contentWidthPx = 1280,
    stylesheetHrefs: string[] = []
): string => {
    const origin = window.location.origin;

    const links = stylesheetHrefs.map(href => `<link rel="stylesheet" href="${href}">`).join('');

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <base href="${origin}/">
    <title>${title}</title>
    ${links}
    <style>${inlineCss}</style>
  </head>
  <body class="bg-white text-gray-900" style="margin:0;padding:0;">
    <div class="export-pdf" style="width:${contentWidthPx}px;margin:0 auto;">${markup}</div>
  </body>
</html>`;
};

/** Small helper to convert CSS units to pixels for scaling computation. */
const pxFromUnit = (value: string): number => {
    const v = value.trim();
    if (v.endsWith('px')) {
        return parseFloat(v);
    }
    if (v.endsWith('mm')) {
        return (parseFloat(v) / 25.4) * 96;
    }
    if (v.endsWith('cm')) {
        return (parseFloat(v) / 2.54) * 96;
    }
    if (v.endsWith('in')) {
        return parseFloat(v) * 96;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const generatePdfBlobFromElement = async (
    element: HTMLElement,
    options?: ExportOptions,
    title?: string
): Promise<Blob> => {
    const fragment = element?.outerHTML ?? '<div />';
    const inlineCss = await buildInlineCss();
    const stylesheetHrefs = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map(link => (link as HTMLLinkElement).href)
        .filter(Boolean);
    const padding = options?.padding ?? '12mm';
    const desktopWidthPx = options?.contentWidthPx ?? 1280;
    const html = buildHtmlDocument(
        fragment,
        title,
        `${inlineCss}\nbody{padding:${padding}}`,
        desktopWidthPx,
        stylesheetHrefs
    );
    const token = getToken();

    const pdfOptions = {
        format: 'a4',
        printBackground: true,
        margin: { top: padding, right: padding, bottom: padding, left: padding },
    } as const;

    // Compute scale so desktop width fits printable A4 width (page width - margins)
    const a4WidthPx = 8.27 * 96; // ~794px
    const padPx = pxFromUnit(padding);
    const printablePx = Math.max(0, a4WidthPx - 2 * padPx);
    const scale = Math.max(0.4, Math.min(1, printablePx / desktopWidthPx));

    const res = await fetch('/api/pdf', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            html,
            options: {
                pdf: { ...pdfOptions, scale },
                waitUntil: 'networkidle2',
                emulateScreenMedia: false,
            },
        }),
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`PDF generation failed (${res.status}): ${text}`);
    }

    return res.blob();
};

const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

export const generatePdfBase64FromElement = async (
    element: HTMLElement,
    options?: ExportOptions,
    title?: string
): Promise<string> => {
    const blob = await generatePdfBlobFromElement(element, options, title);
    return blobToBase64(blob);
};

export const generateAndDownloadPdf = async (
    element: HTMLElement | null,
    filename = 'export.pdf',
    options?: ExportOptions,
    title?: string
) => {
    if (!element) {
        throw new Error('No element provided to generate PDF from');
    }

    const blob = await generatePdfBlobFromElement(element, options, title);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

export default generateAndDownloadPdf;
